import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/** An optional action button rendered inside a toast (e.g. "Undo" after a delete). */
export type ToastAction = {
  label: string;
  onClick: () => void;
};

type ToastOptions = {
  action?: ToastAction;
  /** Override the auto-dismiss delay; action toasts default to a longer window. */
  durationMs?: number;
};

type Toast = {
  id: number;
  message: string;
  action?: ToastAction;
};

type ToastContextValue = {
  showToast: (message: string, options?: ToastOptions) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_LIFETIME_MS = 3000;
// Action toasts (e.g. the Undo after deleting an import) stay longer so the
// affordance comes to the user instead of vanishing before they can click it.
const ACTION_TOAST_LIFETIME_MS = 8000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, options?: ToastOptions) => {
      idRef.current += 1;
      const id = idRef.current;
      const action = options?.action;
      setToasts((prev) => [...prev, { id, message, action }]);
      const lifetime =
        options?.durationMs ??
        (action !== undefined ? ACTION_TOAST_LIFETIME_MS : TOAST_LIFETIME_MS);
      window.setTimeout(() => {
        dismiss(id);
      }, lifetime);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="region"
        aria-label="Notifications"
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className="pointer-events-auto flex items-center gap-3 rounded-md border border-outline bg-surface-container px-4 py-2 text-sm text-on-surface shadow-md"
          >
            <span>{t.message}</span>
            {t.action !== undefined && (
              <button
                type="button"
                className="shrink-0 font-medium text-primary underline-offset-2 hover:underline"
                onClick={() => {
                  t.action?.onClick();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error("useToast must be used inside a ToastProvider");
  }
  return ctx;
}
